/**
 * Woven awnings, market cloth and market goods (lifted from lantern-water's
 * `market-props.js`): `shopCanopy` (the apothecary's striped awning),
 * `marketCloth` (a stall's sagging striped canopy on its frame) and
 * `marketStock` (the stall's slatted counter with baskets of produce, or with
 * books and rolled charts). Every sewn strip is a tensioned surface with a
 * bound, rolled hem; all strips share one woven cloth material (vertex
 * colours carry the stripes), so the three awnings join one batch. The cloth
 * texture and the produce, basket and paper finishes are made once per kit.
 */
import * as THREE from 'three';
import type { Ctx, SceneMaterials, Vec3 } from './kit.ts';

const hash = (x: number, y: number): number => {
  const v = Math.sin(x * 37.17 + y * 13.41) * 43758.5453;
  return v - Math.floor(v);
};

type Finishes = {
  readonly cloth: THREE.MeshStandardMaterial;
  readonly produce: THREE.MeshStandardMaterial;
  readonly basket: THREE.MeshStandardMaterial;
  readonly paper: THREE.MeshStandardMaterial;
  readonly cube: THREE.BoxGeometry;
  readonly fruit: THREE.IcosahedronGeometry;
};

const finishes = (ctx: Ctx): Finishes => {
  const k = ctx.kit;
  const map = k.shared('market-props:cloth-map', () => {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const g = canvas.getContext('2d');
    if (g !== null) {
      g.fillStyle = '#d6d1bc';
      g.fillRect(0, 0, 128, 128);
      for (let y = 0; y < 128; y += 1) {
        for (let x = 0; x < 128; x += 1) {
          g.fillStyle = (x + y) % 2 ? '#fff7da17' : '#4b463619';
          g.fillRect(x, y, 1, 1);
          if (hash(x, y) > 0.985) {
            g.fillStyle = '#786a4630';
            g.fillRect(x, y, 1, 3);
          }
        }
      }
      for (let y = 0; y < 128; y += 1) {
        for (let x = 0; x < 128; x += 1) {
          const bleach = Math.max(0, Math.sin(x * 0.061 + y * 0.012)) * 0.055;
          const damp = Math.exp(-Math.pow((x - 7 - Math.sin(y * 0.037) * 2) / 6, 2)) * 0.07;
          g.fillStyle = `rgba(249,236,202,${bleach})`;
          g.fillRect(x, y, 1, 1);
          g.fillStyle = `rgba(76,69,45,${damp})`;
          g.fillRect(x, y, 1, 1);
        }
      }
      g.fillStyle = '#6b62404a';
      g.fillRect(2, 0, 1, 128);
      g.fillRect(125, 0, 1, 128);
      for (let y = 0; y < 128; y += 5) {
        g.fillStyle = '#f3e9c78c';
        g.fillRect(4, y, 1, 2);
        g.fillRect(123, y, 1, 2);
      }
    }
    const t = new THREE.CanvasTexture(canvas);
    t.colorSpace = THREE.SRGBColorSpace;
    t.magFilter = THREE.NearestFilter;
    t.minFilter = THREE.NearestMipmapLinearFilter;
    return t;
  });
  return {
    cloth: k.shared('market-props:cloth', () => new THREE.MeshStandardMaterial({ map, vertexColors: true, roughness: 0.97, side: THREE.DoubleSide, forceSinglePass: true })),
    produce: k.shared('market-props:produce', () => new THREE.MeshStandardMaterial({ color: '#ffffff', vertexColors: true, roughness: 0.75 })),
    basket: k.shared('market-props:basket', () => new THREE.MeshStandardMaterial({ color: '#a78b60', roughness: 0.97 })),
    paper: k.shared('market-props:paper', () => new THREE.MeshStandardMaterial({ color: '#dfd1ab', roughness: 1 })),
    cube: k.shared('market-props:cube', () => new THREE.BoxGeometry(1, 1, 1)),
    fruit: k.shared('market-props:fruit', () => new THREE.IcosahedronGeometry(1, 1)),
  };
};

const add = (p: THREE.Object3D, geometry: THREE.BufferGeometry, material: THREE.Material, x: number, y: number, z: number, sx = 1, sy = sx, sz = sx): THREE.Mesh => {
  const o = new THREE.Mesh(geometry, material);
  o.position.set(x, y, z);
  o.scale.set(sx, sy, sz);
  o.castShadow = true;
  o.receiveShadow = true;
  p.add(o);
  return o;
};

const up = new THREE.Vector3(0, 1, 0);
const rod = (p: THREE.Object3D, a: Vec3, b: Vec3, r: number, m: THREE.Material): THREE.Mesh => {
  const start = new THREE.Vector3(...a);
  const end = new THREE.Vector3(...b);
  const delta = end.clone().sub(start);
  const mid = start.clone().add(end).multiplyScalar(0.5);
  const mesh = add(p, new THREE.CylinderGeometry(r, r, delta.length(), 6), m, mid.x, mid.y, mid.z);
  mesh.quaternion.setFromUnitVectors(up, delta.normalize());
  return mesh;
};

const colorGeometry = <G extends THREE.BufferGeometry>(geometry: G, color: string, variation = 0): G => {
  const c = new THREE.Color(color);
  const position = geometry.getAttribute('position');
  const colors: number[] = [];
  for (let i = 0; i < position.count; i += 1) {
    const shade = 1 - variation * (0.5 + 0.5 * Math.sin(position.getX(i) * 7 + position.getY(i) * 3));
    colors.push(c.r * shade, c.g * shade, c.b * shade);
  }
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  return geometry;
};

type Sample = (x: number, t: number) => Vec3;

const fabricStrip = (f: Finishes, parent: THREE.Object3D, sample: Sample, left: number, width: number, color: string, stripe: number): void => {
  const positions: number[] = [];
  const uv: number[] = [];
  const indices: number[] = [];
  const colors: number[] = [];
  const tint = new THREE.Color(color).multiplyScalar(0.96 + hash(stripe, 19) * 0.04);
  for (let row = 0; row <= 12; row += 1) {
    for (let col = 0; col <= 6; col += 1) {
      const u = col / 6;
      const t = row / 12;
      positions.push(...sample(left + u * width, t));
      uv.push(u, t);
      colors.push(tint.r, tint.g, tint.b);
    }
  }
  for (let row = 0; row < 12; row += 1) {
    for (let col = 0; col < 6; col += 1) {
      const a = row * 7 + col;
      indices.push(a, a + 7, a + 1, a + 1, a + 7, a + 8);
    }
  }
  const surface = (points: number[], coords: number[], faces: number[], shades: number[]): void => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(coords, 2));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(shades, 3));
    geometry.setIndex(faces);
    geometry.computeVertexNormals();
    add(parent, geometry, f.cloth, 0, 0, 0);
  };
  surface(positions, uv, indices, colors);
  const hp: number[] = [];
  const hu: number[] = [];
  const hi: number[] = [];
  const hc: number[] = [];
  const binding = tint.clone().multiplyScalar(0.62);
  for (let col = 0; col <= 8; col += 1) {
    const u = col / 8;
    const pt = sample(left + u * width, 1);
    const drop = 0.12 + Math.sin(u * Math.PI) * 0.13;
    [0, drop - 0.035, drop - 0.035, drop].forEach((dy, row) => {
      hp.push(pt[0], pt[1] - dy, pt[2] + (row === 0 ? 0 : row === 3 ? 0.008 : 0.02));
      hu.push(u, 1 - dy * 1.5);
      const c = row < 2 ? tint : binding;
      hc.push(c.r, c.g, c.b);
    });
    if (col < 8) {
      const a = col * 4;
      hi.push(a, a + 1, a + 4, a + 4, a + 1, a + 5);
      hi.push(a + 2, a + 3, a + 6, a + 6, a + 3, a + 7);
    }
  }
  surface(hp, hu, hi, hc);
};

const box = (f: Finishes, p: THREE.Object3D, x: number, y: number, z: number, w: number, h: number, d: number, m: THREE.Material): THREE.Mesh => add(p, f.cube, m, x, y, z, w, h, d);

/** The apothecary's striped awning, at (x, y, z) in `parent`'s space. */
export const shopCanopy = (ctx: Ctx, parent: THREE.Object3D, x: number, y: number, z: number, width: number, depth: number): THREE.Group => {
  const f = finishes(ctx);
  const materials: SceneMaterials = ctx.m;
  const group = new THREE.Group();
  group.name = 'woven-apothecary-awning';
  group.position.set(x, y, z);
  parent.add(group);
  const stripes = 10;
  const sw = width / stripes;
  const sample: Sample = (xx, t) => {
    const u = (xx + width / 2) / width;
    const a = (t * Math.PI) / 2;
    const pleat = Math.cos(u * stripes * Math.PI * 2) * 0.022 * Math.sin(a);
    return [xx, -0.47 * (1 - Math.cos(a)) - Math.sin(u * Math.PI) * Math.sin(t * Math.PI) * 0.045 + pleat, depth * Math.sin(a)];
  };
  for (let stripe = 0; stripe < stripes; stripe += 1) fabricStrip(f, group, sample, -width / 2 + stripe * sw, sw, stripe % 2 ? '#f1e2bd' : '#638575', stripe);
  for (const side of [-1, 1]) rod(group, [side * (width / 2 - 0.16), -0.6, depth - 0.03], [side * (width / 2 - 0.16), -0.1, 0], 0.045, materials.iron);
  box(f, group, 0, 0.025, 0, width + 0.16, 0.095, 0.12, materials.darkWood);
  return group;
};

/** A market stall's sagging striped canopy and its frame (red, or green over the book stall). */
export const marketCloth = (ctx: Ctx, p: THREE.Object3D, books = false): void => {
  const f = finishes(ctx);
  const materials = ctx.m;
  const stripes = 10;
  const width = 3.36;
  const sw = width / stripes;
  const depth = 1.9;
  const sample: Sample = (x, t) => {
    const u = (x + width / 2) / width;
    const fold = Math.cos(((x + width / 2) / sw) * Math.PI * 2) * (0.016 + Math.sin(t * Math.PI) * 0.02);
    return [x, 3.1 - t * 0.62 - Math.sin(t * Math.PI) * 0.11 - Math.sin(u * Math.PI) * 0.055 + fold, -0.95 + t * depth];
  };
  for (let stripe = 0; stripe < stripes; stripe += 1) fabricStrip(f, p, sample, -width / 2 + stripe * sw, sw, stripe % 2 ? '#f1e2bd' : books ? '#658578' : '#ae6558', stripe);
  for (const zz of [-0.7, 0.7]) {
    const t = (zz + 0.95) / depth;
    const yy = sample(0, t)[1] - 0.11;
    rod(p, [-1.36, yy, zz], [1.36, yy, zz], 0.04, materials.darkWood);
    for (const side of [-1, 1]) {
      const xx = side * 1.3;
      box(f, p, xx, yy / 2, zz, 0.095, yy, 0.095, materials.wood);
      box(f, p, xx, 0.16, zz, 0.135, 0.17, 0.135, materials.iron);
      rod(p, [xx, yy - 0.44, zz], [xx - side * 0.34, yy - 0.035, zz], 0.035, materials.wood);
      rod(p, [xx, yy, zz], [xx, sample(xx, t)[1] + 0.018, zz], 0.016, materials.canvas);
    }
  }
};

const basket = (f: Finishes, p: THREE.Object3D, x: number, z: number, material: THREE.Material): void => {
  const profile = (
    [
      [0, 0],
      [0.22, 0],
      [0.25, 0.04],
      [0.33, 0.27],
      [0.32, 0.3],
      [0.295, 0.3],
      [0.29, 0.26],
      [0.22, 0.055],
      [0, 0.055],
    ] as const
  ).map(([a, b]) => new THREE.Vector2(a, b));
  add(p, new THREE.LatheGeometry(profile, 16), f.basket, x, 0.78, z);
  for (let i = 0; i < 6; i += 1) {
    const rim = add(p, new THREE.TorusGeometry(0.25 + i * 0.013, 0.012, 4, 20), material, x, 0.825 + i * 0.043, z);
    rim.rotation.x = Math.PI / 2;
  }
  for (let i = 0; i < 16; i += 1) {
    const a = (i * Math.PI) / 8;
    rod(p, [x + Math.sin(a) * 0.25, 0.82, z + Math.cos(a) * 0.25], [x + Math.sin(a) * 0.325, 1.065, z + Math.cos(a) * 0.325], 0.009, f.basket);
  }
  const rim = add(p, new THREE.TorusGeometry(0.32, 0.025, 5, 24), f.basket, x, 1.073, z);
  rim.rotation.x = Math.PI / 2;
};

/** A stall's slatted counter and its goods: three baskets of produce, or books and rolled charts. */
export const marketStock = (ctx: Ctx, p: THREE.Object3D, books = false): void => {
  const f = finishes(ctx);
  const materials = ctx.m;
  for (let i = 0; i < 12; i += 1) box(f, p, -1.375 + i * 0.25, 0.39, 0.78, 0.23, 0.62, 0.065, i % 3 ? materials.wood : materials.darkWood);
  for (const y of [0.15, 0.62]) box(f, p, 0, y, 0.83, 3.04, 0.075, 0.065, materials.darkWood);
  for (const x of [-1.31, 1.31]) {
    box(f, p, x, 0.37, 0.2, 0.12, 0.74, 1.17, materials.wood);
    for (const z of [-0.28, 0.7]) box(f, p, x, 0.38, z, 0.13, 0.75, 0.13, materials.darkWood);
  }
  if (!books) {
    for (let k = 0; k < 3; k += 1) {
      const x = -0.94 + k * 0.94;
      const z = 0.47;
      basket(f, p, x, z, materials.wood);
      for (let i = 0; i < 11; i += 1) {
        const a = i * 2.4;
        const rad = i > 6 ? 0.1 : 0.2;
        const xx = x + Math.sin(a) * rad;
        const zz = z + Math.cos(a) * rad;
        const yy = i > 6 ? 1.145 : 1.06;
        const geo = colorGeometry(f.fruit.clone(), ['#bf5845', '#809950', '#d3ab55'][k] ?? '#bf5845', 0.16);
        add(p, geo, f.produce, xx, yy, zz, 0.085, k === 1 ? 0.12 : 0.084, 0.085);
        rod(p, [xx, yy + 0.08, zz], [xx + 0.013, yy + 0.125, zz], 0.009, materials.darkWood);
      }
    }
    return;
  }
  const piles: readonly (readonly [number, number, number, number])[] = [
    [0, -0.99, 0.43, 4],
    [1, -0.3, 0.31, 3],
    [2, 0.93, 0.46, 5],
  ];
  for (const [pile, x, z, count] of piles) {
    for (let i = 0; i < count; i += 1) {
      const o = new THREE.Group();
      o.position.set(x, 0.79 + i * 0.08, z);
      o.rotation.y = (hash(i, pile) - 0.5) * 0.25;
      p.add(o);
      const m = [materials.book1, materials.book2, materials.book3][(pile + i) % 3] ?? materials.book1;
      box(f, o, 0, 0.035, 0, 0.49, 0.052, 0.36, f.paper);
      for (const y of [0, 0.071]) box(f, o, 0, y, 0, 0.53, 0.02, 0.39, m);
      box(f, o, -0.26, 0.037, 0, 0.03, 0.074, 0.39, m);
      for (const zz of [-0.12, 0.12]) box(f, o, -0.278, 0.037, zz, 0.012, 0.069, 0.014, materials.brass);
    }
  }
  for (let i = 0; i < 3; i += 1) {
    const xx = 0.26 + i * 0.15;
    const zz = 0.27 + i * 0.065;
    const roll = add(p, new THREE.CylinderGeometry(0.054, 0.054, 0.53, 10), f.paper, xx, 0.86, zz);
    roll.rotation.x = Math.PI / 2;
    const tie = add(p, new THREE.TorusGeometry(0.057, 0.009, 4, 12), materials.darkWood, xx, 0.86, zz);
    tie.rotation.y = 0.2;
  }
  box(f, p, 0.18, 0.792, 0.78, 0.54, 0.018, 0.3, f.paper);
  for (let i = 0; i < 5; i += 1) box(f, p, 0.08 + i * 0.065, 0.805, 0.78, 0.01, 0.008, 0.22, materials.book1);
};
